import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Wallet, MessageSquare, History, MoreHorizontal, Newspaper, FlaskConical, Bell, NotebookPen, Settings, LogOut, Sparkles, X, GraduationCap, Radar } from "lucide-react";
import { ModeSwitch } from "@/components/Beginner";
import { ExplainPage } from "@/components/ExplainPage";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { PlanBadge } from "@/components/Locked";
import { isNativeApp } from "@/lib/platform";

const PRIMARY = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard-mobile" },
  { to: "/portfolio", label: "Portfolio", icon: Wallet, tid: "nav-portfolio-mobile" },
  { to: "/chat", label: "AI Research", icon: MessageSquare, tid: "nav-chat-mobile" },
  { to: "/performance", label: "Performance", icon: History, tid: "nav-performance-mobile" },
];
const MORE = [
  { to: "/opportunities", label: "Opportunities", icon: Radar, tid: "nav-opportunities-mobile" },
  { to: "/news", label: "News", icon: Newspaper, tid: "nav-news-mobile" },
  { to: "/alerts", label: "Alerts", icon: Bell, tid: "nav-alerts-mobile" },
  { to: "/journal", label: "Journal", icon: NotebookPen, tid: "nav-journal-mobile" },
  { to: "/strategy-lab", label: "Strategy Lab", icon: FlaskConical, tid: "nav-lab-mobile" },
  { to: "/learn", label: "Learn Crypto", icon: GraduationCap, tid: "nav-learn-mobile" },
  { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings-mobile" },
];

export const MobileNav = ({ user, plan, onLogout }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const moreActive = MORE.some((m) => pathname.startsWith(m.to)) || pathname.startsWith("/pricing");
  const item = "flex flex-col items-center justify-center gap-1 min-h-[56px] text-[10px] select-none";
  return (
    <>
      <nav data-testid="mobile-nav" className="fixed bottom-0 left-0 right-0 z-40 md:hidden apx-glass border-t"
           style={{ borderColor: "var(--border-1)", paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="grid grid-cols-5">
          {PRIMARY.map(({ to, label, icon: Icon, tid }) => (
            <NavLink key={to} to={to} data-testid={tid} className={({ isActive }) => `${item} ${isActive ? "text-cyan-300" : "text-slate-400"}`}>
              <Icon className="w-5 h-5" /> {label}
            </NavLink>
          ))}
          <button data-testid="nav-more-mobile" onClick={() => setOpen(true)} className={`${item} ${moreActive ? "text-cyan-300" : "text-slate-400"}`} aria-label="More">
            <MoreHorizontal className="w-5 h-5" /> More
          </button>
        </div>
      </nav>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[#0F141C] border-slate-800 text-slate-100 rounded-t-2xl p-0 md:hidden" data-testid="mobile-more-sheet"
                      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <SheetHeader className="px-5 pt-4 pb-2 text-left">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <img src="/brand/evidex-logo-notag.jpg" alt="EV-IDEX" className="h-11 w-auto rounded-md shrink-0" data-testid="brand-logo-sheet" />
                <div className="min-w-0">
                  <SheetTitle className="font-display text-base truncate">{user?.name || "EV-IDEX"}</SheetTitle>
                  <SheetDescription className="text-xs text-slate-500 truncate">{user?.email}</SheetDescription>
                </div>
              </div>
              <div className="flex items-center gap-2"><ExplainPage compact /><ModeSwitch compact /><PlanBadge plan={plan} /><button onClick={() => setOpen(false)} className="btn-ghost rounded-lg p-2" aria-label="Close" data-testid="mobile-more-close"><X className="w-4 h-4" /></button></div>
            </div>
          </SheetHeader>
          <div className="grid grid-cols-3 gap-2 px-4 pb-2">
            {MORE.map(({ to, label, icon: Icon, tid }) => (
              <NavLink key={to} to={to} data-testid={tid} onClick={() => setOpen(false)}
                className={({ isActive }) => `flex flex-col items-center gap-2 rounded-xl border p-3 min-h-[76px] text-xs ${isActive ? "border-cyan-900/60 bg-slate-800/70 text-cyan-300" : "border-slate-800 bg-slate-900/50 text-slate-200"}`}>
                <Icon className="w-5 h-5" /> {label}
              </NavLink>
            ))}
            {!isNativeApp() && (
              <NavLink to="/pricing" data-testid="nav-pricing-mobile" onClick={() => setOpen(false)}
                className="flex flex-col items-center gap-2 rounded-xl border border-cyan-800/60 bg-cyan-500/10 p-3 min-h-[76px] text-xs text-cyan-200">
                <Sparkles className="w-5 h-5" /> {plan === "free" ? "Upgrade" : "Plans"}
              </NavLink>
            )}
          </div>
          <button data-testid="mobile-logout-btn" onClick={() => { setOpen(false); onLogout(); }}
                  className="mx-4 mb-4 mt-1 w-[calc(100%-2rem)] rounded-xl border border-slate-800 bg-slate-900/50 py-3 text-sm text-slate-300 inline-flex items-center justify-center gap-2 min-h-[48px]">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
};
